import React, { useCallback, useEffect, useRef, useState } from 'react';
import UiSlider from './ui/UiSlider';
import { L } from '../utils/logger';

export interface MixTransportProps {
  duration: number;          // seconds
  currentTime: number;       // seconds
  status: 'idle' | 'loading' | 'playing' | 'paused';
  // Effective media playback rate (1.0 = normal). When playing, seek bar will advance at this rate.
  mediaRate?: number;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (time: number) => void; // commit seek
}

function fmt(t: number) {
  if (!isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

export default function MixTransport({
  duration,
  currentTime,
  status,
  mediaRate = 1.0,
  onPlay,
  onPause,
  onStop,
  onSeek,
}: MixTransportProps) {
  const [localTime, setLocalTime] = useState(() => clamp(currentTime, 0, Math.max(duration || 0, 0)));
  const draggingRef = useRef(false);
  const lastCommittedRef = useRef<number>(localTime);
  const statusRef = useRef(status);
  // Used to force-restart the rAF ticker after a drag/seek while playing
  const [rafEpoch, setRafEpoch] = useState(0);
  useEffect(() => { statusRef.current = status; }, [status]);
  // Keep local slider in sync with engine when NOT dragging and NOT playing.
  useEffect(() => {
    if (draggingRef.current) return;
    if (status === 'playing') return; // prevent fighting the rAF while playing

    const end = Math.max(duration || 0, 0);
    const next = clamp(currentTime, 0, end);

    // tiny epsilon to avoid no-op writes
    setLocalTime(prev => (Math.abs(prev - next) > 0.001 ? next : prev));
  }, [currentTime, duration, status]);

  // rAF ticker: one loop, stable deps, cancel on unmount/rerun.
  // IMPORTANT: do NOT include currentTime here (keeps the loop stable).
  useEffect(() => {
    if (status !== 'playing') return;
    if (draggingRef.current) return;

    const end = Math.max(duration || 0, 0);
    let raf = 0;
    let running = true;
    let t0 = performance.now();

    // Start from what's currently shown to keep UI buttery
    const base = clamp(localTime, 0, end);
    let lastEmit = -Infinity;

    const tick = () => {
      if (!running || draggingRef.current) return;
      const dt = (performance.now() - t0) / 1000;
      const rate = Number.isFinite(mediaRate) && mediaRate > 0 ? mediaRate : 1.0;
      const t = clamp(base + dt * rate, 0, end);

      // ~30fps throttle + no-op guard
      if (t - lastEmit > 1 / 30) {
        lastEmit = t;
        setLocalTime(prev => (Math.abs(prev - t) > 0.001 ? t : prev));
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => { running = false; if (raf) cancelAnimationFrame(raf); };
    // Intentionally NOT depending on currentTime/localTime to keep loop stable
  }, [status, duration, mediaRate, rafEpoch]);

  // Slider handlers
  const handleSliderChange = useCallback((v: number) => {
    setLocalTime(clamp(v, 0, Math.max(duration || 0, 0)));
  }, [duration]);

  const commitSeek = useCallback((t?: number) => {
    const target = clamp(t ?? localTime, 0, Math.max(duration || 0, 0));
    if (!isFinite(target)) return;
    if (Math.abs(target - lastCommittedRef.current) < 1e-3) return; // no-op
    lastCommittedRef.current = target;
    onSeek(target);
    // If we were playing, the rAF loop might have stopped during drag; restart it
    if (statusRef.current === 'playing') {
      setRafEpoch((e) => e + 1);
    }
    try { L.info('media:action', 'seek.commit', { to: target }); } catch {}
  }, [duration, localTime, onSeek]);

  const handlePointerDown: React.PointerEventHandler<HTMLInputElement> = useCallback(() => {
    draggingRef.current = true;
  }, []);

  const handlePointerUp: React.PointerEventHandler<HTMLInputElement> = useCallback((e) => {
    draggingRef.current = false;
    commitSeek();
  }, [commitSeek]);

  // Keyboard: Enter commits, Esc cancels (revert to engine time)
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
    if (e.key === 'Enter') {
      draggingRef.current = false;
      commitSeek();
    } else if (e.key === 'Escape') {
      draggingRef.current = false;
      setLocalTime(clamp(currentTime, 0, Math.max(duration || 0, 0)));
    }
  }, [commitSeek, currentTime, duration]);

  // Commit on blur (covers mouse release outside, touch cancel, etc.)
  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(() => {
    if (draggingRef.current) {
      draggingRef.current = false;
      commitSeek();
    }
  }, [commitSeek]);

  const endLabel = fmt(duration);
  const curLabel = fmt(localTime);
  const pauseDisabled = status === 'idle' || status === 'loading';
  const pauseClass = pauseDisabled
    ? 'inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-700 bg-transparent text-neutral-400 cursor-not-allowed'
    : status === 'paused'
      ? 'inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-700 bg-amber-600 text-neutral-900'
      : 'inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-700 bg-neutral-900 hover:bg-amber-600 text-neutral-200';

  return (
    <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
      {/* Controls group: center on mobile, inline on md+ */}
      <div className="flex items-center gap-2 justify-center w-full md:w-auto order-1 md:order-none">
        <button
        type="button"
        onClick={() => {
          const t0 = performance.now();
          try { L.info('media:action', 'play.click'); } catch {}
          onPlay();
          setTimeout(() => {
            if (statusRef.current !== 'playing') {
              try { L.warn('media:action', 'play.slow-state', { ms: Math.round(performance.now() - t0), current: statusRef.current }); } catch {}
            }
          }, 600);
        }}
        disabled={status === 'playing'}
        title="Play"
        aria-label="Play"
  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-700 bg-neutral-900 hover:bg-emerald-700 text-neutral-200 disabled:bg-emerald-800 disabled:text-white disabled:cursor-not-allowed"
        >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
          <polygon points="9,6 18,12 9,18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        </button>
        <button
        type="button"
        onClick={() => {
          const t0 = performance.now();
          try { L.info('media:action', 'pause.click'); } catch {}
          onPause();
          setTimeout(() => {
            if (statusRef.current !== 'paused') {
              try { L.warn('media:action', 'pause.slow-state', { ms: Math.round(performance.now() - t0), current: statusRef.current }); } catch {}
            }
          }, 600);
        }}
        disabled={pauseDisabled}
        title="Pause"
        aria-label="Pause"
        className={pauseClass}
        >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
          <line x1="10" y1="6" x2="10" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="14" y1="6" x2="14" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        </button>
        <button
        type="button"
        onClick={() => {
          const t0 = performance.now();
          try { L.info('media:action', 'stop.click'); } catch {}
          onStop();
          setTimeout(() => {
            const cur = statusRef.current;
            if (!(cur === 'paused' || cur === 'idle')) {
              try { L.warn('media:action', 'stop.slow-state', { ms: Math.round(performance.now() - t0), current: cur }); } catch {}
            }
          }, 600);
        }}
        disabled={status === 'idle' || status === 'loading'}
        title="Stop"
        aria-label="Stop"
  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-700 bg-neutral-900 hover:bg-red-700 text-neutral-200 disabled:bg-red-800 disabled:text-white disabled:cursor-not-allowed"
        >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
          <rect x="8" y="8" width="8" height="8" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
        </button>
      </div>

      {/* Timeline group: slider spans full width; labels below */}
      <div className="w-full md:flex-1 md:w-auto order-2 md:order-none">
        <UiSlider
          value={localTime}
          min={0}
          max={Math.max(duration || 0, 0.001)}
          step={0.01}
          onChange={handleSliderChange}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={!isFinite(duration) || duration <= 0}
          className="w-full"
          aria-label="Timeline"
          aria-valuetext={`${curLabel} of ${endLabel}`}
        />
        <div className="flex justify-between mt-1 text-[10px] text-neutral-400">
          <span>{curLabel}</span>
          <span>{endLabel}</span>
        </div>
      </div>
    </div>
  );
}
